// https://tarekraafat.github.io/autoComplete.js/#/configuration
// https://tarekraafat.github.io/autoComplete.js/#/how-to-guides?id=multiple-choices
import autoComplete from "@tarekraafat/autocomplete.js"

/**
 * @param {Boolean} force - If true allows to enter an option not included in the result list, if false only accepts options in the result list
 * @param {Boolean} searchOnArrowDown - If true opens and searches on arrow down key
 */
export default () => ({
  autocomplete: null,
  selected: [],
  force: true,
  searchOnArrowDown: false,
  isSearching: false,
  init(){
    const input = this.$root.querySelector('input[type=hidden]')

    this.force = input.getAttribute('force') === 'true'
    this.searchOnArrowDown = input.getAttribute('searchOnArrowDown') === 'true'

    // fill autocomplete options
    // https://github.com/alpinejs/alpine/blob/main/packages/alpinejs/src/evaluator.js#L61
    const optionsEvaluator = new Function(`return ${this.$el.getAttribute('options')}`)
    const options = {
      placeHolder: input.placeholder,
      data: {
        src: [],
        keys: []
      },
      ...optionsEvaluator()
    }

    // set initial value(s)
    try{
      this.selected = JSON.parse(input.value)
    }catch(e){
      this.selected = []
    }
    this.$watch('selected', (newValue) => input.value = JSON.stringify(newValue))

    // set relative container
    this.$el.style.position = 'relative'
    // set main configs
    const configs = {
      ...options,
      selector: () => this.$refs.search, // force selector as search
      threshold: this.searchOnArrowDown ? 0 : (options.threshold || 1), // force zero-char search if searchOnArrowDown
      data: {
        ...options.data,
        src: async (q) => {
          this.isSearching = true
          // run function for results or use static array
          const opts = typeof options.data.src === 'function' ?
                    (options.data.src.constructor.name === "AsyncFunction" ? await options.data.src(q) : options.data.src(q))
                    : options.data.src

          this.isSearching = false

          return opts.filter(el => !this.selected.map(e => e.value).includes(el.value))
          // proxy array comparison
          // return opts.filter(el => !this.selected.map((e) => JSON.stringify(e)).includes(JSON.stringify(el)))
        },
        keys: ['value'],
      },
      wrapper: false,
      resultsList: {
        ...options.resultsList,
        class: 'autocomplete-list',
      },
      resultItem: {
        ...options.resultItem,
        selected: 'selected',
      }
    }

    // init
    this.autocomplete = new autoComplete(configs)

    this.$el.addEventListener('selection', (e) => {
      const selection = e.detail.selection.value
      this.onAdd(selection)
    })

    this.$el.addEventListener('keydown', (e) => {
      if(e.key === 'ArrowDown' && this.searchOnArrowDown && this.$refs.search.value === '' && !(this.autocomplete.cursor >= 0)) this.autocomplete.start()
      if(e.key === 'Enter' && this.$refs.search.value != '' && (!this.autocomplete.cursor || this.autocomplete.cursor < 0)) {
        if (!this.force) { this.onAdd({value: this.$refs.search.value}) } else { this.$refs.search.value = '' }
      }
      if(e.key === 'Backspace' && this.$refs.search.value == '' && this.selected.length) this.onRemove(this.selected.at(-1))
    })

    this.searchfield.addEventListener('blur', (e) => {
      if (this.$refs.search.value != '' && !this.force) { this.onAdd({value: this.$refs.search.value}) } else { this.$refs.search.value = '' }
    })

    this.$el.addEventListener('open', () => {
      // calc direction on open
      const select = this.$root.querySelector('ul.autocomplete-list')
      const yPos = select.getBoundingClientRect().y
      const selectHeight = select.getBoundingClientRect().height
      select.dataset.direction = yPos + selectHeight > window.innerHeight ? 'top' : 'bottom'
    })
    this.$el.addEventListener('close', () => {
      // reset direction
      const select = this.$root.querySelector('ul.autocomplete-list')
      delete select.dataset.direction
    })
  },
  onAdd(item){
    if(this.selected.find(el => el.value == item.value)) return

    const search = this.searchfield
    this.selected.push(item)
    search.value = ''
    search.focus()
    this.hasChanged()
  },
  onRemove(item){
    this.selected = this.selected.filter(el => el.value != item.value)
    this.hasChanged()
  },
  hasChanged(){
    const input = this.$root.querySelector('input[type=hidden]')
    this.$nextTick(() => input.dispatchEvent(new Event('change')))
    this.closeSearch()
  },
  closeSearch(){
    this.autocomplete.cursor = -1
    this.autocomplete.close()
  },
  get searchfield(){
    return this.autocomplete.input
  }
})